/**
 * Realism levels. One data object per level; every package reads flags from here instead
 * of testing the level name, so the meaning of "mid" lives in one place.
 */
import type { RealismLevel } from './types';

export interface RealismOptions {
  level: RealismLevel;
  // --- sim: compliance ---
  /** Seconds between the ATC transmission and the pilot acting, [min, max]. */
  complianceDelayS: [number, number];
  /** Probability a readback is wrong (heading/altitude digit swapped); the controller must repeat. */
  wrongReadbackRate: number;
  // --- sim: flight model ---
  /** Per-type performance instead of one table per category. */
  typeSpecificPerformance: boolean;
  /** Turn rate limited by bank angle at speed (else a flat 3°/s). */
  bankLimitedTurns: boolean;
  /** Climb rate falls off with altitude. */
  altitudeDependentClimb: boolean;
  wind: 'none' | 'steady' | 'gusts';
  // --- sim: procedures ---
  /** Arrivals enter on their STAR and fly it (every level now; kept for custom realism). Otherwise straight in from the scope edge. */
  arrivalsFollowStar: boolean;
  /** Arrivals honour the STAR altitude/speed restrictions on their own. */
  descendVia: boolean;
  /**
   * Steepest intercept heading, degrees off the final approach course, on which the crew captures
   * the localizer. Steeper, the aircraft flies through the final and reports it did not intercept
   * (PANS-ATM 8.9.4 allows 45° at most). 90 keeps the forgiving capture of any closing heading.
   */
  maxInterceptDeg: number;
  /** Go around if not stabilised (too high or too fast) on short final. */
  unstableApproachGoAround: boolean;
  /** Go around if the runway is still occupied by a landing or departing aircraft. */
  runwayOccupancyCheck: boolean;
  /** Wake-turbulence spacing behind a heavier category counts as a separation loss. */
  wakeSeparation: boolean;
  /** Leaving the airspace volume or entering a restricted area counts against the score. */
  airspaceCheck: boolean;
  /** Pilots ask for lower/higher when left level too long (radio chatter only). */
  pilotRequests: boolean;
  /** Multiplies how long an unanswered pilot waits before calling again, pressing and acting alone (sim/src/patience.ts). */
  pilotPatienceScale: number;
  /** Chance a pilot call uses wording other than the canonical one (sim/src/phraseology.ts). 0 keeps every call identical. */
  phraseVariation: number;
  // --- radar scope ---
  /** Runway length multiplier for drawing (ATC-SIM enlarges them). */
  runwayDrawScale: number;
  showVideoMap: boolean;
  showWindInHeader: boolean;
  /** Seconds between position updates on the scope; 0 = every frame. */
  radarSweepS: number;
  /** Seconds the mode-C altitude readout lags. */
  altitudeReadoutLagS: number;
  // --- tower view ---
  towerQuality: 0 | 1 | 2 | 3 | 'auto';
}

export const REALISM: Record<RealismLevel, RealismOptions> = {
  low: {
    level: 'low',
    complianceDelayS: [0, 0], wrongReadbackRate: 0,
    typeSpecificPerformance: false, bankLimitedTurns: false, altitudeDependentClimb: false, wind: 'none',
    arrivalsFollowStar: true, descendVia: false, maxInterceptDeg: 90, unstableApproachGoAround: false, runwayOccupancyCheck: false,
    wakeSeparation: false, airspaceCheck: false, pilotRequests: false, pilotPatienceScale: 1.5, phraseVariation: 0.4,
    runwayDrawScale: 4, showVideoMap: false, showWindInHeader: false, radarSweepS: 0, altitudeReadoutLagS: 0,
    towerQuality: 1,
  },
  mid: {
    level: 'mid',
    complianceDelayS: [2, 5], wrongReadbackRate: 0,
    typeSpecificPerformance: true, bankLimitedTurns: true, altitudeDependentClimb: false, wind: 'steady',
    arrivalsFollowStar: true, descendVia: false, maxInterceptDeg: 45, unstableApproachGoAround: true, runwayOccupancyCheck: false,
    wakeSeparation: true, airspaceCheck: false, pilotRequests: false, pilotPatienceScale: 1, phraseVariation: 0.5,
    runwayDrawScale: 2, showVideoMap: true, showWindInHeader: true, radarSweepS: 0, altitudeReadoutLagS: 0,
    towerQuality: 2,
  },
  high: {
    level: 'high',
    complianceDelayS: [3, 8], wrongReadbackRate: 0.03,
    typeSpecificPerformance: true, bankLimitedTurns: true, altitudeDependentClimb: true, wind: 'gusts',
    arrivalsFollowStar: true, descendVia: true, maxInterceptDeg: 45, unstableApproachGoAround: true, runwayOccupancyCheck: true,
    wakeSeparation: true, airspaceCheck: true, pilotRequests: true, pilotPatienceScale: 0.75, phraseVariation: 0.6,
    runwayDrawScale: 1, showVideoMap: true, showWindInHeader: true, radarSweepS: 4, altitudeReadoutLagS: 4,
    towerQuality: 'auto',
  },
};

export function realismOptions(level: RealismLevel | RealismOptions): RealismOptions {
  return typeof level === 'string' ? REALISM[level] : level;
}
